import { AreaCategory as AreaCategoryModel, Gym } from 'fitbeat.models';

export type AreaCategory = AreaCategoryModel;

export const additionalCalisthenicAreaZones: AreaCategory[] = [
  'dumbbell',
  'benchAndDumbbell',
  'boxing',
];

export const staticMachineAreaZones: AreaCategory[] = [
  'treadmill',
  'bicycle',
  'step',
  'elliptical',
  'rower',
  'power',
];

export const staticFunctionalAreaZones: AreaCategory[] = [
  'calisthenic',
  'dumbbell',
  'benchAndDumbbell',
  'boxing',
];

interface IZoneType {
  name: string;
  area: AreaCategory;
}

export class ZoneMapper {
  public static getAllZoneTypeNumbers() {
    return Object.keys(ZONE_TYPE_MAPPING).map((key: string) => Number(key));
  }

  public getZoneTypesForNumberInGym(numberInGym: number, gym: Gym): number[] {
    if (!numberInGym) {
      return [];
    }
    const mappings = gym.zoneMapping;
    return mappings[numberInGym - 1] ?? [];
  }

  public getZoneTypeForOfflineHydration(numberInGym: number, gym: Gym, loop?: number): number {
    const zoneTypeNumbers = gym.zoneMapping[numberInGym - 1];
    if (!loop) {
      return zoneTypeNumbers[0];
    }
    const index = loop % zoneTypeNumbers.length;
    return zoneTypeNumbers[index];
  }

  public getZoneName(zoneTypeNumber: number): string {
    const zoneType = ZONE_TYPE_MAPPING[zoneTypeNumber];
    return zoneType?.name || 'No Name';
  }
}

const ZONE_TYPE_MAPPING: { [key: number]: IZoneType } = {
  1: { name: 'Speedway', area: 'circuit' },
  2: { name: 'Zoo', area: 'circuit' },
  3: { name: 'Penitentiary', area: 'circuit' },
  4: { name: 'Crossrig', area: 'circuit' },
  5: { name: 'Chamber', area: 'circuit' },
  6: { name: 'Lake', area: 'circuit' },
  7: { name: 'Dark Alley', area: 'circuit' },
  8: { name: 'Octagon', area: 'circuit' },
  9: { name: 'Slopes', area: 'circuit' },
  10: { name: 'Centre Court', area: 'circuit' },
  11: { name: 'Field', area: 'circuit' },
  12: { name: 'Basement', area: 'circuit' },
  13: { name: 'Colosseum', area: 'circuit' },
  14: { name: 'Cage', area: 'circuit' },
  15: { name: 'Bootcamp', area: 'circuit' },
  16: { name: 'Studio', area: 'circuit' },
  17: { name: 'The Ring', area: 'circuit' },
  18: { name: 'Playground', area: 'circuit' },
  19: { name: 'Construction Site', area: 'circuit' },
  20: { name: 'Track', area: 'circuit' },
  21: { name: 'Slopes', area: 'circuit' },
  22: { name: 'Penitentiary', area: 'circuit' },
  23: { name: 'OutdoorC', area: 'circuit' },
  24: { name: 'OutdoorD', area: 'circuit' },
  25: { name: 'OutdoorE', area: 'circuit' },
  26: { name: 'OutdoorF', area: 'circuit' },
  27: { name: 'OutdoorG', area: 'circuit' },
  28: { name: 'OutdoorH', area: 'circuit' },
  29: { name: 'OutdoorI', area: 'circuit' },
  30: { name: 'OutdoorJ', area: 'circuit' },
  31: { name: 'HomeA', area: 'circuit' },
  32: { name: 'HomeB', area: 'circuit' },
  33: { name: 'HomeC', area: 'circuit' },
  34: { name: 'HomeD', area: 'circuit' },
  35: { name: 'HomeE', area: 'circuit' },
  36: { name: 'HomeF', area: 'circuit' },
  37: { name: 'HomeG', area: 'circuit' },
  38: { name: 'HomeH', area: 'circuit' },
  39: { name: 'HomeI', area: 'circuit' },
  40: { name: 'HomeJ', area: 'circuit' },
  41: { name: 'HomeK', area: 'circuit' },
  42: { name: 'HomeL', area: 'circuit' },
  43: { name: 'HomeM', area: 'circuit' },
  44: { name: 'HomeN', area: 'circuit' },
  45: { name: 'HomeO', area: 'circuit' },
  46: { name: 'HomeP', area: 'circuit' },
  47: { name: 'HomeQ', area: 'circuit' },
  48: { name: 'HomeR', area: 'circuit' },
  49: { name: 'HomeS', area: 'circuit' },
  50: { name: 'HomeT', area: 'circuit' },
  100: { name: 'treadmill', area: 'treadmill' },
  101: { name: 'bicycle', area: 'bicycle' },
  102: { name: 'step', area: 'step' },
  103: { name: 'elliptical', area: 'elliptical' },
  104: { name: 'rower', area: 'rower' },
  105: { name: 'dumbbell', area: 'dumbbell' },
  106: { name: 'benchAndDumbbell', area: 'benchAndDumbbell' },
  107: { name: 'calisthenic', area: 'calisthenic' },
  108: { name: 'boxing', area: 'boxing' },
  109: { name: 'power', area: 'power' },
};

export function getStaticZoneNumberFromArea(zoneName: string): number | undefined {
  if (zoneName === 'circuit') {
    return undefined;
  }
  for (const [key, { name, area }] of Object.entries(ZONE_TYPE_MAPPING)) {
    if (area === zoneName) {
      return Number(key);
    }
    // For some circuit issues in testing?
    if (name === zoneName) {
      return Number(key);
    }
  }
  return undefined;
}

export function getKnownAreaCategories(): AreaCategory[] {
  const result = new Set<AreaCategory>();
  for (const { area } of Object.values(ZONE_TYPE_MAPPING)) {
    result.add(area);
  }
  return [...result.values()];
}

export function hasExerciseForZoneScreen(zoneTypeNumber: number): boolean {
  if (zoneTypeNumber === 111 || zoneTypeNumber === 222 || zoneTypeNumber === 333) {
    // these type numbers are used in workout hydration tests to mean 'some arbitrary zone type'
    return true;
  }
  const area = getAreaCategory(zoneTypeNumber);

  return !staticMachineAreaZones.includes(area);
}

export function isStaticCardioZone(input: number | AreaCategory | undefined): boolean {
  if (typeof input === 'number' || input === undefined) {
    const area = getAreaCategory(input);
    return staticMachineAreaZones.includes(area);
  }
  return staticMachineAreaZones.includes(input);
}

export function isStaticFunctionalZone(input: number | AreaCategory | undefined): boolean {
  if (typeof input === 'number' || input === undefined) {
    const area = getAreaCategory(input);
    return staticFunctionalAreaZones.includes(area);
  }
  return staticFunctionalAreaZones.includes(input);
}

export function isCalisthenicAreaZone(input: number | AreaCategory | undefined): boolean {
  if (typeof input === 'number' || input === undefined) {
    const area = getAreaCategory(input);
    return additionalCalisthenicAreaZones.includes(area);
  }
  return additionalCalisthenicAreaZones.includes(input);
}

export function isCalisthenicZone(input?: number) {
  return getAreaCategory(input) === 'calisthenic';
}

export function isBoxingZone(input?: number) {
  return getAreaCategory(input) === 'boxing';
}

export function getAreaCategory(zoneType: number | number[] | undefined): AreaCategory {
  if (zoneType && typeof zoneType !== 'number') {
    // a zone will contain either circuit or a specific static type
    // so we can use the first zone type to determine area category
    const firstZoneType = zoneType[0];
    return getAreaCategory(firstZoneType);
  }
  if (zoneType === 111 || zoneType === 222 || zoneType === 333) {
    // these type numbers are used in workout hydration tests to mean 'some arbitrary zone type'
    // TODO: replace these values in tests with valid zoneTypes
    return 'circuit';
  }
  if (zoneType === undefined) {
    return 'circuit';
  }

  const zoneTypeInfo = ZONE_TYPE_MAPPING[zoneType];
  if (!zoneTypeInfo) {
    throw new Error(`invalid zone type ${zoneType}!`);
  }
  return zoneTypeInfo.area;
}

export function getAreaCategoriesForEachZoneScreenInGym(zoneMapping: number[][]): AreaCategory[] {
  const result: AreaCategory[] = [];
  for (const zoneTypes of zoneMapping) {
    const areaCategory = getAreaCategory(zoneTypes);
    result.push(areaCategory);
  }
  return result;
}

export function getUniqueCircuitAndStaticAreas(zoneTypeNumbers: number[]) {
  const circuitZoneTypeNumbers = new Set<number>();
  const staticFunctionalAreas = new Set<AreaCategory>();
  const staticMachineAreas = new Set<AreaCategory>();

  for (const zoneTypeNumber of zoneTypeNumbers) {
    const area = getAreaCategory(zoneTypeNumber);
    if (staticFunctionalAreaZones.includes(area)) {
      staticFunctionalAreas.add(area);
      if (isCalisthenicAreaZone(area)) {
        staticFunctionalAreas.add('calisthenic');
      }
    } else if (staticMachineAreaZones.includes(area)) {
      staticMachineAreas.add(area);
    } else {
      circuitZoneTypeNumbers.add(zoneTypeNumber);
    }
  }

  return {
    circuitZoneTypeNumbers: [...circuitZoneTypeNumbers],
    staticFunctionalAreas: [...staticFunctionalAreas],
    staticMachineAreas: [...staticMachineAreas],
  };
}

export function getAmountOfStationsForCategory(zoneMapping: number[][], category: AreaCategory): number {
  const areaCategories = getAreaCategoriesForEachZoneScreenInGym(zoneMapping);
  const zonesForCategory = areaCategories.filter((a: AreaCategory) => a === category).length;
  if (category === 'circuit') {
    return zonesForCategory * 2;
  }
  return zonesForCategory;
}

export function isStaticZone(zoneTypeNumber: number): boolean {
  const area = getAreaCategory(zoneTypeNumber);
  return area !== 'circuit';
}

export const zoneTypeNumberOrderForWorkoutDesign: number[] = [
  22, 5, 10, 3, 15, 11, 13, 7, 23, 21, 8, 14, 19, 20, 18, 12, 1, 2, 4, 6, 9, 16, 17,
];

export const getAllGymAreaCategories = (zoneMapping: number[][]): AreaCategory[] => {
  const areaSet: Set<AreaCategory> = new Set();
  for (const zoneTypes of zoneMapping) {
    areaSet.add(getAreaCategory(zoneTypes));
  }
  return Array.from(areaSet);
};

export function getAllGymAreaCategoriesInPriorityOrder(zoneMapping: number[][]): AreaCategory[] {
  const allAreas = getAllGymAreaCategories(zoneMapping);
  const ordered: Set<AreaCategory> = new Set();

  function addAreaNextIfInGym(area: AreaCategory) {
    if (allAreas.includes(area)) {
      ordered.add(area);
    }
  }

  // priority order
  addAreaNextIfInGym('circuit');
  addAreaNextIfInGym('benchAndDumbbell');
  addAreaNextIfInGym('dumbbell');
  addAreaNextIfInGym('calisthenic');
  addAreaNextIfInGym('boxing');
  // all the rest
  for (const area of allAreas) {
    ordered.add(area);
  }

  return Array.from(ordered);
}

const getAllKnownAreaCategoriesForGym = (gym: Gym) => {
  const uniqueZonesInGym = new Set(gym.zoneMapping.flat(1));
  const uniqueAreas = new Set<AreaCategory>();
  for (const zone of uniqueZonesInGym) {
    try {
      const areaCategory = getAreaCategory(zone);
      uniqueAreas.add(areaCategory);
    } catch {
      // do nothing
    }
  }
  return uniqueAreas;
};

export const isGymMultiAreaGym = (gym: Gym): boolean => {
  const areasInGym = getAllKnownAreaCategoriesForGym(gym);
  return areasInGym.size > 1;
};

export const ZoneMappings = new ZoneMapper();
